import { type Common } from '../common.js';
import { type FlowRunBlockWithData } from '../models/flow/flowRun.js';
import {
  type FlowRunExploreCountOptions,
  type FlowRunExploreCountResponse,
  type FlowRunExploreDownloadResponse,
  type FlowRunExploreExportOptions,
  type FlowRunExploreExportResponse,
  type FlowRunExploreFindAllOptions,
  type FlowRunExploreFindAllResponse,
  type FlowRunExploreGetColumnsOptions,
  type FlowRunExploreGetColumnsResponse,
  type FlowRunExploreRunShell,
  type FlowRunExportJobCreateResponse,
  type FlowRunExportJobDownloadResponse,
  type FlowRunExportJobListOptions,
  type FlowRunExportJobListResponse,
} from '../models/flow/flowRunExplore.js';
export class API {
  common: Common;
  baseEndpoint = '/flow/flow-runs/explore';

  constructor(common: Common) {
    this.common = common;
  }

  // POSTs to `/search` rather than GETting the base path: a large column
  // projection sent as query params overflows the URL/header size limit and 431s.
  async findAll(body: FlowRunExploreFindAllOptions): Promise<FlowRunExploreFindAllResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/search`);
    return await this.common.postJSON(url, body);
  }

  async getCount(queryParams?: FlowRunExploreCountOptions): Promise<FlowRunExploreCountResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/count`, queryParams);
    return await this.common.getJSON(url);
  }

  async getColumns(
    queryParams?: FlowRunExploreGetColumnsOptions
  ): Promise<FlowRunExploreGetColumnsResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/columns`, queryParams);
    return await this.common.getJSON(url);
  }

  async export(body?: FlowRunExploreExportOptions): Promise<FlowRunExploreExportResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/export`);
    return await this.common.postJSON(url, body);
  }

  async download(body?: FlowRunExploreExportOptions): Promise<FlowRunExploreDownloadResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/download`);
    return await this.common.postJSON(url, body);
  }

  async createExportJob(
    body?: FlowRunExploreExportOptions
  ): Promise<FlowRunExportJobCreateResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/export-jobs`);
    return await this.common.postJSON(url, body);
  }

  async findExportJobs(
    queryParams?: FlowRunExportJobListOptions
  ): Promise<FlowRunExportJobListResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/export-jobs`, queryParams);
    return await this.common.getJSON(url);
  }

  async getExportJobDownloadUrl(jobId: string): Promise<FlowRunExportJobDownloadResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/export-jobs/${jobId}/download`);
    return await this.common.getJSON(url);
  }

  async findOneRunById(flowRunId: string): Promise<{ flowRun: FlowRunExploreRunShell }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/runs/${flowRunId}`);
    return await this.common.getJSON(url);
  }

  async findOneBlockById(flowRunId: string, blockId: string): Promise<FlowRunBlockWithData> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/runs/${flowRunId}/blocks/${blockId}`
    );
    const response: { flowRunBlock: FlowRunBlockWithData } = await this.common.getJSON(url);
    return response.flowRunBlock;
  }
}
